import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { useReducer, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import Accordion from '../../../components/Accordion';
import { buttonSubmitStyle } from '../../../components/ButtonSubmit';
import RoundShadowBox from '../../../components/RoundShadowBox';
import { CustomH3, HTMLText } from '../../../components/SEO';
import { adminPropTypes } from '../../../contexts/Admin/constants';
import { colors } from '../../../styles/constants';

const { brand, grey50, grey200, primaryLight100, red, white } = colors;

const Grid = styled.div`
  display: grid;
  grid-gap: 1.5rem;
`;

const Header = styled.div`
  border-bottom: 1px solid ${grey200};
  display: grid;
  grid-template-columns: repeat(2, max-content) 1fr;
  min-height: 6rem;
`;

const HeaderTab = styled.button`
  ${tw`text-headerTab`}

  padding: 0 1.5rem;

  ${({ active }) =>
    active &&
    `box-shadow: inset 0 -2px 0 0 ${brand}; 
    font-weight: 700;`}
`;

const Editor = styled.div`
  ${tw`mb-def md:mb-abs6`}
  display: grid;
  grid-gap: 1.5rem;
`;

const Label = styled.label`
  ${tw`text-headerTab font-bold`}
`;

const inputStyle = css`
  background-color: ${grey50};
  padding: 1.5rem;

  &:focus {
    outline-color: ${brand};
    ${tw`shadow-medium`};
  }
`;

const StyledTextareaAutosize = styled(TextareaAutosize)`
  ${inputStyle}
`;

const PriorityTab = styled.div`
  ${tw`text-headerTab`}
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 1.5rem;
  justify-self: end;
`;

const Priority = styled.input`
  ${inputStyle}
  ${tw`h-full w-abs6`}
  padding: 0 1.5rem;

  ${({ errorMessage }) => errorMessage && `border: 1px solid ${red} ;`}

  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    opacity: 1;
  }
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  margin-top: 1.5rem;
`;

const Buttons = styled.div`
  display: grid;
  grid-gap: 3rem;
  grid-template-columns: 2fr 1fr;
`;

const Button = styled.button`
  ${buttonSubmitStyle};
  user-select: none;

  ${({ disabled }) =>
    disabled &&
    `background-color: ${primaryLight100};
    
    &:hover {
      background-color: ${primaryLight100};
    }`}
`;

const DeleteButton = styled(Button)`
  background: ${white};
  box-shadow: inset 0 0 0 3px ${red};
  color: ${red};
  justify-self: end;
  max-width: fit-content;

  &:hover {
    color: ${white};
    background: ${red};
  }
`;

const ErrorMessage = styled(RoundShadowBox)`
  ${tw`p-def text-sm`}
  border: 1px solid ${red};
  color: ${red};
  justify-self: center;
  max-width: fit-content;
`;

/**
 * @typedef {Object} EditableContentProps
 * @property {Object} admin The object containing the functions and variables related to the administrators.
 * @property {Object} initialState The initial state of the editor.
 * @property {function} deleteEditableContent Redux's action that delete the editable content.
 * @property {string} state Required to define the primary key.
 */

/**
 * @description Displays an editor to update content of the /search pages.
 * @param {EditableContentProps} props
 * @returns {JSX.Element}
 */
function EditableContent({
  admin,
  deleteEditableContent,
  initialState,
  state,
}) {
  const initialEditorState = {
    ...initialState,
    errorMessage: null,
    isModified: false,
    isNew: !!initialState.isNew,
    isPreview: !initialState.isNew,
  };

  function editorReducer(state, { payload, type }) {
    switch (type) {
      case 'setContent':
        const content = {
          ...state.content,
          [payload.key]: payload.value,
        };

        return {
          ...state,
          content,
          isModified: !isEqual(content, editorState.content),
        };

      case 'setErrorMessage':
        return { ...state, errorMessage: payload };

      case 'setIsPreview':
        return { ...state, isPreview: payload };

      case 'setPriority':
        return {
          ...state,
          errorMessage: null,
          isModified: payload !== editorState.priority,
          priority: payload,
        };

      case 'save':
        return { ...editorState, ...payload };

      case 'cancel':
        return editorState;

      default:
        return state;
    }
  }

  const [editorState, setEditorState] = useState(initialEditorState);

  const [
    { content, errorMessage, id, isModified, isNew, isPreview, priority },
    dispatch,
  ] = useReducer(editorReducer, editorState);

  const { body, title } = content;

  /**
   * @description Sets the error message in the editor's state.
   * @param {string} errorMessage
   * @returns {void}
   */
  function setErrorMessage(errorMessage) {
    dispatch({ type: 'setErrorMessage', payload: errorMessage });
  }

  /**
   * @description Sets the `key` of the content to `value` in the editor's state.
   * @param {'body' | 'title'|} key
   * @param {string} value
   * @returns {void}
   */
  function setContent(key, value) {
    dispatch({ type: 'setContent', payload: { key, value } });
  }

  /**
   * @description Sets isPreview in the editor's state.
   * @param {boolean} isPreview
   * @returns {void}
   */
  function setIsPreview(isPreview) {
    dispatch({ type: 'setIsPreview', payload: isPreview });
  }

  /**
   * @description Sets the priority in the editor's state.
   * @param {number} priority
   * @returns {void}
   */
  function setPriority(priority) {
    dispatch({ type: 'setPriority', payload: priority });
  }

  /**
   * @description Sets the state of the editor after an API request.
   * @param {Object} response The API request post create or update.
   * @returns {void}
   */
  function save(response) {
    const { content, id, priority } = response;

    const onSaveState = {
      content,
      error: null,
      id,
      isNew: false,
      isPreview: true,
      priority,
    };

    setEditorState({
      ...initialEditorState,
      ...onSaveState,
    });

    dispatch({
      type: 'save',
      payload: onSaveState,
    });
  }

  /**
   * @description Calls the admin function that creates the editable content according to the editor's state.
   * @returns {Promise<void>}
   */
  async function onCreate() {
    const [error, response] = await admin.createEditableContent({
      content: { body, title },
      priority,
      state,
    });

    if (error) {
      return setErrorMessage(error.errorMessage);
    }

    return save(response);
  }

  /**
   * @description Calls the admin function that updated the editable content according to the editor's state.
   * @returns {Promise<void>}
   */
  async function onUpdate() {
    const response = await admin.updateEditableContent(id, {
      content: { body, title },
      priority,
    });

    return save(response);
  }

  /**
   * @description Cancels changes to the editor's status.
   * @note Deletes the component if it is new.
   */
  async function onCancel() {
    dispatch({ type: 'cancel' });

    isNew && deleteEditableContent();
  }

  /**
   * @description Calls the admin function that delete the current editable content accordint to his id.
   * @note Calls the redux action to delete it too.
   */
  async function onDelete() {
    await admin.deleteEditableContent(id);

    deleteEditableContent();
  }

  return (
    <Grid>
      <Header>
        <HeaderTab active={!isPreview} onClick={() => setIsPreview(false)}>
          Edit
        </HeaderTab>
        <HeaderTab active={isPreview} onClick={() => setIsPreview(true)}>
          Preview
        </HeaderTab>
        {!isPreview && (
          <PriorityTab>
            <Label htmlFor="priority">Priority</Label>
            <Priority
              error={errorMessage}
              id="priority"
              min="0"
              onChange={(event) => setPriority(Number(event.target.value))}
              type="number"
              value={priority}
            />
          </PriorityTab>
        )}
      </Header>
      {isPreview ? (
        <Accordion
          content={<HTMLText dangerouslySetInnerHTML={{ __html: body }} />}
          header={<CustomH3>{title}</CustomH3>}
          open
        />
      ) : (
        <Editor>
          <Label htmlFor="title">Title</Label>
          <StyledTextareaAutosize
            autoFocus
            id="title"
            onChange={(event) => setContent('title', event.target.value)}
            placeholder="Content title without HTML tags (e.g. Que faire quand il pleut ?)"
            value={title}
          />
          <Label htmlFor="body">Body</Label>
          <StyledTextareaAutosize
            id="body"
            minRows={3}
            onChange={(event) => setContent('body', event.target.value)}
            placeholder="Content in HTML (e.g. <p>Sortez vos <strong>parapluies</strong> !</p>)"
            value={body}
          />
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <Actions>
            <Buttons>
              <Button
                disabled={!isModified || !body || !title}
                onClick={isNew ? onCreate : onUpdate}
              >
                {isNew ? 'Create' : 'Update'}
              </Button>
              <Button onClick={onCancel}>Cancel</Button>
            </Buttons>
            {!isNew && <DeleteButton onClick={onDelete}>Delete</DeleteButton>}
          </Actions>
        </Editor>
      )}
    </Grid>
  );
}

EditableContent.propTypes = {
  admin: adminPropTypes,
  deleteEditableContent: PropTypes.func.isRequired,
  initialState: PropTypes.shape({
    content: PropTypes.shape({
      body: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
    id: PropTypes.number,
    isNew: PropTypes.bool,
    priority: PropTypes.number.isRequired,
  }).isRequired,
  state: PropTypes.string.isRequired,
};

export default EditableContent;
